import { relativeTime } from '@/features/notifications/relativeTime';
import { ACTIVITY_META } from './presenceMeta';
import type { ActivityState } from './types';

/**
 * The states where "last seen" is worth saying — and the only ones.
 *
 * On any other state it is noise at best and wrong at worst: "Available · last
 * seen 2 hours ago" reads as a contradiction, and for In a call or Presenting
 * the person is demonstrably present this second.
 */
const ABSENT: ReadonlySet<ActivityState> = new Set<ActivityState>([
  'away',
  'beRightBack',
  'offline',
]);

/** A last-seen younger than this is a clock arguing with itself, not a fact. */
const MIN_AGE_MS = 60_000;

/**
 * "Last seen 15 minutes ago", or `null` when the phrase would be a lie.
 *
 * Lives apart from `PresenceLine` so the one surface that CANNOT render a line —
 * a bare presence dot on a chat row or an avatar — can still carry the same
 * sentence in its accessible name and tooltip without a second implementation
 * of the rule drifting away from this one.
 */
export function lastSeenClause(state: ActivityState, lastActivityAt: number | null): string | null {
  if (!ABSENT.has(state) || lastActivityAt === null) return null;
  if (Date.now() - lastActivityAt <= MIN_AGE_MS) return null;
  return `Last seen ${relativeTime(lastActivityAt)}`;
}

/**
 * The whole fact as one string: "Away · Last seen 15 minutes ago".
 *
 * For places where presence is drawn and not written — `PresenceDot`'s label,
 * which is both its accessible name and its tooltip. A colour is not a sentence,
 * and a person who left on Friday must not be indistinguishable from one who
 * stepped out for coffee just because the surface only had room for a dot.
 */
export function presenceTitle(state: ActivityState, lastActivityAt: number | null): string {
  const clause = lastSeenClause(state, lastActivityAt);
  const label = ACTIVITY_META[state].label;
  return clause === null ? label : `${label} · ${clause}`;
}
